import React from 'react';
import styles from './EnhancedMilestoneDisplay.module.css';

// SVG Icon Components
const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MoneyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23 4v6h-6M1 20v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LightbulbIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 21h6M10 21v-3a4 4 0 0 1 4-4 4 4 0 0 1 4 4v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 3a6 6 0 0 0-6 6c0 1.5.5 2.9 1.4 4.1.9 1.2 2.1 2.1 3.6 2.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EnhancedMilestoneDisplay = ({ 
  milestones, 
  goalIndex, 
  highlightedMilestone, 
  onMilestoneClick,
  aiMetadata,
  aiQuality 
}) => {
  if (!milestones || !milestones.data || !milestones.data.recommendations) {
    return <p>Unable to load recommendations.</p>;
  }
  
  // Defensive: Check milestones structure
  const path = milestones.data?.recommendations?.[0]?.path1;
  if (!path || typeof path !== 'object') {
    return <p>No recommendations available.</p>;
  }

  const getMilestoneColor = (stepIndex) => {
    const colors = [
      '#3B82F6', '#06B6D4', '#10B981', '#8B5CF6', 
      '#6366F1', '#0EA5E9', '#14B8A6', '#7C3AED'
    ];
    return colors[stepIndex % colors.length];
  };

  const getQualityScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  // Helper function to safely render content
  const safeRender = (content) => {
    if (typeof content === 'string') return content;
    if (typeof content === 'number') return content.toString();
    if (typeof content === 'object') return JSON.stringify(content);
    return String(content);
  };

  return (
    <div className={styles.enhancedMilestoneContainer}>
      {/* Quality and Metadata Header */}
      {(aiQuality || aiMetadata) && (
        <div className={styles.metadataHeader}>
          {aiQuality && (
            <div className={styles.qualityIndicator}>
              <span className={styles.qualityLabel}>Quality Score:</span>
              <span 
                className={styles.qualityScore}
                style={{ color: getQualityScoreColor(aiQuality.score || aiQuality) }}
              >
                {Math.round(aiQuality.score || aiQuality)}%
              </span>
            </div>
          )}
          {aiMetadata && (
            <div className={styles.metadataInfo}>
              {aiMetadata.provider && (
                <span className={styles.providerTag}>
                  {aiMetadata.provider.toUpperCase()}
                </span>
              )}
              {aiMetadata.error && (
                <span className={styles.errorTag}>Error</span>
              )}
              {aiMetadata.isFallback && (
                <span className={styles.fallbackTag}>Fallback</span>
              )}
              {aiMetadata.generatedAt && (
                <span className={styles.generatedAt}>
                  Generated: {new Date(aiMetadata.generatedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Milestones List */}
      <div className={styles.milestoneList}>
        {Object.entries(path).map(([key, step], stepIndex) => {
          const isHighlighted = highlightedMilestone && 
            highlightedMilestone.goalIndex === goalIndex && 
            highlightedMilestone.milestoneIndex === stepIndex;
          
          return (
            <div 
              key={key} 
              id={`milestone-${goalIndex}-${stepIndex}`}
              className={`${styles.milestoneItem} ${isHighlighted ? styles.highlightedMilestone : ''}`}
              onClick={() => onMilestoneClick && onMilestoneClick(goalIndex, stepIndex, key)}
            >
              {/* Milestone Header */}
              <div className={styles.milestoneHeader}>
                <div 
                  className={styles.milestoneMarker} 
                  style={{ backgroundColor: getMilestoneColor(stepIndex) }}
                >
                  {stepIndex + 1}
                </div>
                <div className={styles.milestoneTitleSection}>
                  <h5 className={styles.milestoneTitle}>{safeRender(step.title)}</h5>
                  <div className={styles.milestoneMeta}>
                    {step.timeline && (
                      <span className={styles.timelineBadge}>
                        {safeRender(step.timeline)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Milestone Description - The Hero Content */}
              <div className={styles.milestoneDescription}>
                <p className={styles.descriptionText}>{safeRender(step.details)}</p>
              </div>

              {/* Insider Tips - Only show if available */}
              {Array.isArray(step.insider_tips) && step.insider_tips.length > 0 && (
                <div className={styles.insiderTip}>
                  <span className={styles.tipIcon}>💡</span>
                  <p className={styles.tipText}>{safeRender(step.insider_tips[0])}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EnhancedMilestoneDisplay; 